<template>
  <div class="relative flex w-full" :style="textareaStyles">
    <div v-if="required" class="absolute right-0 pr-1 text-lg text-error">
      *
    </div>
    <div v-if="tooltip">
      <InformationCircleIcon
        class="absolute right-0 bottom-0 mb-1 mr-1 text-info w-4 h-4"
        v-tooltip="tooltip"
      />
    </div>
    <textarea
      :id="label || id"
      :for="label || id"
      :type="type"
      ref="input"
      :required="required"
      class="ai-input-class ai-textarea-dynamic-ring peer pt-2.5 min-h-[70px]"
      :class="[
        error ? 'ai-input-class-error' : '',
        internalBgColor
      ]"
      :placeholder="placeholder"
      :aria-describedby="label || id"
      v-model="internalValue"
      :disabled="disabled"
    />
    <label
      :for="id || label"
      class="ai-input-label peer-focus:px-2 peer-placeholder-shown:text-neutral-content peer-placeholder-shown:scale-100 peer-placeholder-shown:top-7 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:font-normal rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
      :class="[
        {
          'opacity-0 invisible': !internalValue || internalValue.length === 0,
        },
        internalBgColor
      ]"
    >
      {{ label }}
    </label>
  </div>
</template>

<script setup>
import { computed, inject } from "vue"
import { InformationCircleIcon } from "@heroicons/vue/24/outline"
import { colorNameToBgClass, colorNameToCssVar } from "@/composables/useColors"

const formSettings = inject('form') ?? {}

const props = defineProps({
  id: {
    type: String,
  },
  modelValue: [String, Number],
  label: {
    type: String,
  },
  placeholder: {
    type: String,
    default: " ",
  },
  type: {
    type: String,
    default: "text",
  },
  required: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  bgColor: {
    type: String,
    default: '',
  },
  borderColor: {
    type: String,
    default: '',
    // Note: For XTextArea, this controls ring color, not actual border
  },
  borderColorFocus: {
    type: String,
    default: '',
    // Note: For XTextArea, this controls focus ring color
  },
  borderColorHover: {
    type: String,
    default: '',
    // Note: For XTextArea, this controls hover ring color
  },
  labelColorFocus: {
    type: String,
    default: '',
  },
})

const internalBgColor = computed(() => {
  const colorName = props.bgColor || formSettings.bgColor || 'base-100'
  return colorNameToBgClass(colorName)
})

const internalRingColor = computed(() => props.borderColor || formSettings.borderColor || 'form-border')
const internalRingColorFocus = computed(() => props.borderColorFocus || formSettings.borderColorFocus || 'form-border-focus')
const internalRingColorHover = computed(() => props.borderColorHover || formSettings.borderColorHover || 'form-border-hover')
const internalLabelColorFocus = computed(() => props.labelColorFocus || formSettings.labelColorFocus || 'base-content')

// Computed styles for dynamic ring and label colors
const textareaStyles = computed(() => ({
  '--ai-textarea-ring': colorNameToCssVar(internalRingColor.value),
  '--ai-textarea-ring-focus': colorNameToCssVar(internalRingColorFocus.value),
  '--ai-textarea-ring-hover': colorNameToCssVar(internalRingColorHover.value),
  '--ai-textarea-label-focus': colorNameToCssVar(internalLabelColorFocus.value)
}))

const emit = defineEmits(["update:modelValue"])

const internalValue = computed({
  get() {
    return props.modelValue
  },
  set(newValue) {
    emit("update:modelValue", newValue)
  },
})
</script>

<style scoped>
textarea {
  resize: vertical;
  height: auto;
  min-height: 70px;
}

.ai-textarea-dynamic-ring {
  --tw-ring-color: var(--ai-textarea-ring);
  transition: box-shadow 200ms;
}

.ai-textarea-dynamic-ring:focus {
  --tw-ring-color: var(--ai-textarea-ring-focus);
}

.ai-textarea-dynamic-ring:hover:not(:focus) {
  --tw-ring-color: var(--ai-textarea-ring-hover);
}

/* Dynamic label focus color */
.ai-textarea-dynamic-ring:focus + .ai-input-label {
  color: var(--ai-textarea-label-focus);
}

.ai-textarea-input-error {
  @apply outline outline-2 outline-offset-2 rounded-md outline-error;
}
</style>
